import React, { useEffect } from 'react';
import { useUrlQuery } from '../../Utils/UrlUtil';
import useAuth from '../../Hooks/useAuth';
import { Loading, openNotification, Modal as ServaModal } from '@servahealth/serva-ui-kit';
import { useMutation } from 'react-query';
import logo from '../../Images/BIUILogo.png';
import Users from '../../Constants/Endpoints/Users';
import AuthenticateUserUsingSSORequest from '../../Interfaces/Requests/Users/AuthenticateUserUsingSSO';
import AxiosUtil from '../../Utils/AxiosUtil';
import AuthResponseModel from '../../Interfaces/Auth/AuthResponseModel';

const ssoAuthStartedKeys = new Set<string>();

const LoginPage: React.FC = () => {
    const url = useUrlQuery();

    const {
        isAuthenticated,
        setIsAuthenticated,
        onLoginSuccess,
        refreshToken,
        refreshAuthFromToken,
        hash,
        setHash,
        lastRedirectToAuth,
    } = useAuth();

    useEffect(() => {
        const checkApiHealth = async () => {
            try {
                await fetch(`${process.env.REACT_APP_API_ROOT}`);

                if (!!url.get('code') && !!hash) {
                    const ssoAuthKey = `${url.get('code') ?? ''}:${hash}`;

                    if (ssoAuthStartedKeys.has(ssoAuthKey)) {
                        return;
                    }

                    ssoAuthStartedKeys.add(ssoAuthKey);

                    authFromSSOToken?.({
                        accessToken: url.get('code') ?? '',
                        onError: redirectToAuth,
                    });
                } else if (refreshToken) {
                    refreshAuthFromToken?.({
                        callback: onLoginSuccess,
                        onError: redirectToAuth,
                    });
                } else {
                    // no refresh auth, so get rid of splash
                    redirectToAuth();
                }
            } catch (error: any) {
                if (
                    error.message === 'Network Error' ||
                    error?.toString() ===
                        'TypeError: NetworkError when attempting to fetch resource.'
                ) {
                    ServaModal.info({
                        title: 'System Maintenance',
                        content: (
                            <div>
                                The System is currently down for maintenance.
                                <br />
                                Please reload the page in a few moments and try
                                again.
                                <br />
                                Thank you for your patience.
                            </div>
                        ),
                        okText: 'Reload',
                        onOk: () => window.location.reload(),
                    });
                }
            }
        };

        checkApiHealth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /*const redirectToAuth = () => {
        const secret = crypto.randomUUID();
        setHash(secret);
        const utf8 = new TextEncoder().encode(secret);
        crypto.subtle.digest('SHA-256', utf8).then((hashBuffer) => {
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hashBase64 = btoa(String.fromCharCode.apply(null, hashArray))
                .replace(/=/g, '')
                .replace(/\+/g, '-')
                .replace(/\//g, '_');
            // purge any expired codes from redirect URL
            let redirectLocation = `${window.location}`;
            if (redirectLocation.includes('code'))
                redirectLocation = redirectLocation
                    .replace('?code=', '')
                    .replace('&code=', '')
                    .replace(url.get('code') ?? '', '');

            window.location.href = `${
                process.env.REACT_APP_SSO
            }/loginredirect?code_challenge=${hashBase64}&code_challenge_method=S256&client_id=${
                process.env.REACT_APP_SSO_CLIENT_ID
            }&redirect_uri=${encodeURI(redirectLocation)}&response_type=code&reason=${lastRedirectToAuth}`;
        });
    };*/


    const redirectToAuth = () =>
    {
        // currently, the signal that this is explicitly a "logout" and not just a "needs sign-in" is the logoutReason
    
        const isLogout = !!lastRedirectToAuth && lastRedirectToAuth !== '';
    
        let redirectLocation = `${window.location}`;
        
        // purge any expired auth tokens from redirect URL (otherwise it might keep trying to exchange an expired token on every login attempt and fail)
        
        if (redirectLocation.includes('code'))
        {
            redirectLocation = redirectLocation.replace('?code=', '').replace('&code=', '').replace(url.get('code') ?? '', '');
        }
        
        // do we need to pre-load the email field on SSO?
        
        const loginHintSearchParam = url.get('login_hint') ? `&login_hint=${encodeURIComponent(url.get('login_hint') ?? '')}` : '';
        
        if (isLogout) 
        {
             // GuardedRoute will parse the reason after SSO logout round trip

             redirectLocation += `${redirectLocation.includes('?') ? '&' : '?'}reason=Last Session Ended Due To: ${lastRedirectToAuth}`;

             if (loginHintSearchParam !== '')
                redirectLocation += loginHintSearchParam; // GuardedRoute will parse the login_hint after SSO logout round trip

             window.location.href = `${process.env.REACT_APP_SSO}/logout?redirect_uri=${encodeURI(redirectLocation)}`
        }
        else
        {
            const secret = crypto.randomUUID();
            setHash(secret);

            // this may be the 2nd "pass" of the logout flow, which we are just pulling from the URL after logout round-trip
            
            const logoutReasonSearchParam = !!url.get('reason') ? `&reason=${encodeURIComponent(url.get('reason') ?? '')}` : '';

            const utf8 = new TextEncoder().encode(secret);
            
            crypto.subtle.digest('SHA-256', utf8).then((hashBuffer) => {
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                const hashBase64 = btoa(String.fromCharCode.apply(null, hashArray)).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
                
                window.location.href = `${process.env.REACT_APP_SSO}/loginredirect?code_challenge=${hashBase64}&code_challenge_method=S256&client_id=${process.env.REACT_APP_SSO_CLIENT_ID}&redirect_uri=${encodeURI(redirectLocation)}&response_type=code${logoutReasonSearchParam}${loginHintSearchParam}`});
        }
    };
    
    useEffect(() => {
        setIsAuthenticated(isAuthenticated);
    }, [isAuthenticated, setIsAuthenticated]);

    interface SsoAuthProps {
        accessToken: string;
        onError: () => void;
    }

    const { mutate: authFromSSOToken } = useMutation(
        ({ accessToken }: SsoAuthProps) => {
            const url = `${process.env.REACT_APP_API_ROOT}${Users.AUTHENTICATE_USER_USING_SSO}`;
            const requestData: AuthenticateUserUsingSSORequest = {
                ssoCode: accessToken,
                ssoChallenge: hash,
                authenticationType: 3,
            };
            return AxiosUtil.post<AuthResponseModel, AuthenticateUserUsingSSORequest>(url, requestData);
        },
        {
            onError: (err: any) => {
                openNotification(
                    'error',
                    'There was a problem trying to login. Please reload the page and try again.'
                );
            },
            onSuccess: (response: AuthResponseModel) => {
                onLoginSuccess(response);
            },
            onSettled: (_, error, { onError }) => {
                if (!!error) onError?.();
            },
        }
    );

    return <Loading logoSrc={logo} />;
};

export default LoginPage;
